import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API, idr, pesanGalat } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Tags, Download, Warehouse, Building2, Eye, EyeOff, Boxes,
  ArrowUpDown, X, AlertTriangle, Info, ChevronLeft, ChevronRight, ShieldCheck, Smartphone,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import PengaturanAksesModal from "@/components/pricelist/PengaturanAksesModal";
import PengaturanHargaUnit from "@/components/pricelist/PengaturanHargaUnit";
import BarisUnit, { KELAS_WARNA } from "@/components/pricelist/BarisUnit";
import FilterProduk, {
  FILTER_PRODUK_KOSONG, TESTID_PRICE_LIST, opsiDariServer,
} from "@/components/produk/FilterProduk";
import { useDebounce } from "@/hooks/useDebounce";

// Peta warna lencana kelas margin (`KELAS_WARNA`) kini SATU sumber di
// `components/pricelist/BarisUnit.jsx` — dipakai baris SKU dan sub-baris unit.

/**
 * Rangkuman Price List — brief owner 10 Sep 2026 poin 1.
 *
 * Semua penyaringan & pengurutan dikirim ke SERVER, tidak ada satu pun yang
 * dikerjakan atas array di layar: daftarnya dipotong per halaman, jadi
 * menyaring di sini akan membuat baris ke-101 tidak pernah ketemu sambil
 * melapor "tidak ada hasil" dengan yakin (jebakan yang sudah tercatat di
 * CLAUDE.md untuk filter piutang/utang).
 *
 * "Mode Daftar Harga" TIDAK menyembunyikan kolom dengan CSS — ia mengirim
 * `sembunyikan_modal=true` sehingga servernya yang tidak pernah mengirim angka
 * modal, dan file Excel-nya benar-benar tidak punya kolom itu. Menyembunyikan
 * di layar saja berarti file yang dikirim ke sales tetap membawa margin.
 *
 * AKSES MODAL & MARGIN (brief owner 18 Sep 2026, batch P): kolom Modal/Unit
 * & Margin, opsi urut margin, dan tombol Mode Daftar Harga SEMUANYA mengikuti
 * flag dari server (`modal_terlihat`, `margin_bentuk`, `boleh_urut_margin`,
 * `boleh_mode_daftar_harga` — `routers/akses_modal.py::putuskan_akses_modal`).
 * Layar ini TIDAK PERNAH menghitung ulang siapa boleh melihat apa. Tombol
 * "Atur Akses Modal & Margin" (superadmin saja) membuka
 * `components/pricelist/PengaturanAksesModal.jsx`, yang bicara ke
 * `routers/inventory/akses_modal_pengaturan.py`.
 *
 * FILTER (brief owner 22 Sep 2026: "tab filter sama persis dengan yg ada pada
 * master produk"): kontrolnya komponen BERSAMA `components/produk/FilterProduk`
 * — bukan salinan — dan pilihan dropdown-nya `opsi` dari server yang diturunkan
 * dari REGISTRY, bukan dari hasil yang sudah tersaring. Yang khas halaman ini
 * (cabang, belum ada harga, rinci per gudang, urutan) ditempel sebagai
 * children / baris kedua.
 *
 * RINCI PER IMEI/SN (brief owner 22 Sep 2026, G2/G3): baris SKU persis seperti
 * "Per barang", lalu daftar unit (IMEI/SN, Battery Health, Garansi, harga per
 * IMEI) di bawahnya — dari `unit[]` respons server. Sub-baris = komponen murni
 * `BarisUnit`. Tombol ubah memakai `can("inventory_produk", "create")`, kunci
 * YANG SAMA dengan `require_permission` rute PATCH dan Bulk Harga.
 */

const inp = "h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 bg-white";
// Owner: "buat bisa sort by gudang, kategori, brand, cabang" — keempatnya ada
// di daftar ini, bukan cuma jadi filter.
const URUT_OPSI = [
  { v: "nama", l: "Nama" }, { v: "sku", l: "SKU" }, { v: "harga", l: "Harga jual" },
  { v: "stok", l: "Stok" }, { v: "margin", l: "Margin" },
  { v: "gudang", l: "Gudang" }, { v: "cabang", l: "Cabang" },
  { v: "kategori", l: "Kategori" }, { v: "brand", l: "Brand" },
];

export default function PriceList() {
  const { can, user } = useAuth();
  const bolehGudang = can("inventory_gudang", "view");
  // Gerbang ROLE, sama dengan backend `require_role("superadmin")` di
  // `akses_modal_pengaturan.py` (pola sama dengan Antrian.jsx:55) — admin
  // pun tidak melihat tombol ini, supaya tidak ada yang berujung 403.
  const bolehAturAkses = user?.role === "superadmin";
  const [aturAksesTerbuka, setAturAksesTerbuka] = useState(false);
  // G2: saklar jawaban Bos no. 23 — gerbang ROLE, sama dengan backend
  // `require_role("superadmin")` di `info_unit.py`.
  const [aturHargaUnitTerbuka, setAturHargaUnitTerbuka] = useState(false);
  // Isi BH/Garansi/harga per IMEI — kunci SAMA dengan rute PATCH (Bulk Harga).
  const bolehUbahUnit = can("inventory_produk", "create");

  const [rinci, setRinci] = useState("sku");
  // qInput = teks kotak cari apa adanya; qApplied = yang dikirim ke server —
  // menyusul setelah 400 ms diam atau langsung saat Enter (pola SAMA dengan
  // Master Produk; dulu setiap ketukan menembak server).
  const [qInput, setQInput] = useState("");
  const [qApplied, setQApplied] = useState("");
  const qDebounced = useDebounce(qInput, 400);
  useEffect(() => { setQApplied(qDebounced); }, [qDebounced]);
  // Filter yang SAMA dengan Master Produk (nama kunci = parameter server).
  const [filter, setFilter] = useState(FILTER_PRODUK_KOSONG);
  const ubahFilter = (patch) => setFilter(f => ({ ...f, ...patch }));
  const [cabang, setCabang] = useState("");
  const [tanpaHarga, setTanpaHarga] = useState(false);
  const [modeDaftarHarga, setModeDaftarHarga] = useState(false);
  const [urut, setUrut] = useState("nama");
  const [arah, setArah] = useState("asc");
  const [page, setPage] = useState(1);

  const [gudang, setGudang] = useState([]);
  const [gudangGagal, setGudangGagal] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierGagal, setSupplierGagal] = useState(false);
  const [cabangOpsi, setCabangOpsi] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unduh, setUnduh] = useState(false);

  // Daftar gudang butuh izin modul terpisah (inventory_gudang) dari halaman ini
  // (inventory_produk) — jangan panggil kalau tidak dipegang, supaya satu 403
  // tidak membuat seluruh halaman kosong. Pola yang sama dipakai KartuStok.jsx.
  // Gagal karena sebab LAIN dicatat, supaya dropdown yang hilang tidak terbaca
  // sebagai "memang belum ada gudang".
  useEffect(() => {
    if (!bolehGudang) return;
    api.get("/inventory/warehouses").then(r => setGudang(r.data.items || []))
      .catch(() => setGudangGagal(true));
  }, [bolehGudang]);

  // Supplier: `GET /supplier` bergerbang inventory_po.view ATAU
  // inventory_produk.view (`supplier_ledger._izin_baca_daftar_supplier`) —
  // halaman ini sendiri butuh inventory_produk.view, jadi selalu boleh.
  useEffect(() => {
    api.get("/supplier").then(r => setSuppliers(r.data.items || []))
      .catch(() => setSupplierGagal(true));
  }, []);

  // Cabang: SATU sumber untuk semua dropdown cabang di aplikasi ini
  // (`/inventory/cabang-options`), gerbangnya cukup login.
  useEffect(() => {
    api.get("/inventory/cabang-options").then(r => setCabangOpsi(r.data.items || [])).catch(() => {});
  }, []);

  // SATU penyusun parameter untuk layar DAN Excel — dua penyusun pasti
  // berpisah jalan, dan isi Excel diam-diam berbeda dari yang terlihat.
  const params = useMemo(() => {
    const p = { rinci, urut, arah };
    const cari = qApplied.trim();
    if (cari) p.q = cari;
    for (const k of ["kategori", "sub_kategori", "brand", "warehouse_id", "supplier", "stok"]) {
      if (filter[k]) p[k] = filter[k];
    }
    if (filter.only_aging) p.only_aging = true;
    if (filter.include_inactive) p.include_inactive = true;
    if (cabang) p.cabang = cabang;
    if (tanpaHarga) p.tanpa_harga = true;
    if (modeDaftarHarga) p.sembunyikan_modal = true;
    return p;
  }, [rinci, urut, arah, qApplied, filter, cabang, tanpaHarga, modeDaftarHarga]);

  const seq = useRef(0);
  const muat = useCallback(async () => {
    const n = ++seq.current;
    setLoading(true);
    try {
      const r = await api.get("/inventory/price-list", { params: { ...params, page, per_page: 100 } });
      // Balasan yang datang terlambat dari filter LAMA tidak boleh menimpa
      // hasil filter yang sedang aktif.
      if (n === seq.current) setData(r.data);
    } catch (e) {
      if (n === seq.current) toast.error(pesanGalat(e, "Gagal memuat price list"));
    } finally {
      if (n === seq.current) setLoading(false);
    }
  }, [params, page]);

  useEffect(() => { muat(); }, [muat]);
  // Setiap perubahan filter mengembalikan ke halaman 1 — kalau tidak, memilih
  // filter baru saat berada di halaman 7 memberi layar kosong yang terlihat
  // seperti "tidak ada hasil".
  useEffect(() => { setPage(1); }, [rinci, qApplied, filter, cabang, tanpaHarga]);

  // Server yang memutuskan urut margin boleh dipakai atau tidak
  // (`boleh_urut_margin`, § LAYAR A DESAIN-BRIEF-18-SEP.md) — kalau berubah
  // jadi tidak boleh (mis. Bos baru saja mencabut akses akun ini) selagi
  // state `urut` masih 'margin', pindahkan sekali ke 'nama' supaya select
  // tidak menampilkan opsi yang sudah dibuang dari daftarnya.
  useEffect(() => {
    if (data && data.boleh_urut_margin === false && urut === "margin") setUrut("nama");
  }, [data, urut]);

  // Balasan PATCH memuat baris unit yang dibangun server dengan keputusan akses
  // akun ini — menggantikan sub-baris apa adanya, tanpa menghitung ulang di sini.
  const simpanUnit = async (unit, ubah, awal) => {
    try {
      const r = await api.patch(`/inventory/price-list/unit/${unit.id}`, { ubah, awal },
        { params: modeDaftarHarga ? { sembunyikan_modal: true } : {} });
      if (r.data?.unit) {
        setData(d => d && ({
          ...d,
          items: (d.items || []).map(it => (it.unit || []).some(x => x.id === unit.id)
            ? { ...it, unit: it.unit.map(x => (x.id === unit.id ? r.data.unit : x)) } : it),
        }));
      }
      toast.success(r.data?.status === "sama" ? "Tidak ada yang berubah" : "Info unit disimpan");
      return true;
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal menyimpan info unit"));
      if (e?.response?.status === 409) muat();
      return false;
    }
  };

  const bersihkan = () => {
    setQInput(""); setQApplied(""); setFilter(FILTER_PRODUK_KOSONG);
    setCabang(""); setTanpaHarga(false);
  };

  const unduhExcel = async () => {
    setUnduh(true);
    try {
      const p = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)]));
      const token = localStorage.getItem("hris_token");
      const res = await fetch(`${API}/inventory/price-list/export-xlsx?${p}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("gagal");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Price_List_${cabang || "semua-cabang"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Price list terunduh");
    } catch {
      toast.error("Gagal mengunduh Excel");
    } finally {
      setUnduh(false);
    }
  };

  // Pilihan dropdown dari REGISTRY (server) — tidak ikut menyusut saat filter
  // dipilih. `kategori_label` juga dipakai menampilkan kategori di tabel dengan
  // label yang sama persis dengan dropdown & Master Produk.
  const opsiFilter = opsiDariServer(data?.opsi);
  const labelKategori = data?.opsi?.kategori_label || {};
  const modalTerlihat = !!data?.modal_terlihat;
  // 'persen' | 'kelas' | null|undefined — SATU flag dari server yang
  // memutuskan bentuk kolom Margin; layar tidak pernah menebaknya sendiri
  // (aturan 3 § LAYAR A DESAIN-BRIEF-18-SEP.md).
  const marginBentuk = data?.margin_bentuk || null;
  const items = data?.items || [];
  // Opsi urut margin cuma muncul kalau server mengizinkan (§ LAYAR A) — bukan
  // ditentukan dari `modalTerlihat` (tingkat 'kelas' boleh urut margin tanpa
  // pernah melihat modal).
  // Mode per IMEI (G2) — ditentukan dari respons server (`rinci` yang benar-benar
  // dipakai), bukan state tombol, supaya kolom & isi baris selalu sepasang.
  const modeImei = data?.rinci === "imei";
  const kolomHargaUnit = !!data?.pengaturan_harga_unit?.kolom_aktif;
  const kolomAksi = modeImei && bolehUbahUnit;
  const jumlahKolom = 8 + (modeImei ? 3 : 0) + (modalTerlihat ? 1 : 0) + (marginBentuk ? 1 : 0)
    + (kolomAksi ? 1 : 0);
  const urutOpsi = data && data.boleh_urut_margin === false
    ? URUT_OPSI.filter((o) => o.v !== "margin")
    : URUT_OPSI;

  const gantiUrut = (kol) => {
    if (urut === kol) setArah(arah === "asc" ? "desc" : "asc");
    else { setUrut(kol); setArah(kol === "nama" || kol === "sku" ? "asc" : "desc"); }
  };
  const Th = ({ kol, children, kanan, testid }) => (
    <th data-testid={testid} className={`px-3 py-2 font-semibold whitespace-nowrap ${kanan ? "text-right" : "text-left"}`}>
      <button type="button" onClick={() => gantiUrut(kol)}
        data-testid={`pl-urut-${kol}`}
        className={`inline-flex items-center gap-1 hover:text-emerald-700 ${urut === kol ? "text-emerald-700" : ""}`}>
        {children}
        <ArrowUpDown className="w-3 h-3 opacity-60" />
      </button>
    </th>
  );

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="halaman-price-list">
      {/* Kepala */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 grid place-items-center">
            <Tags className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Rangkuman Price List</h1>
            <p className="text-sm text-slate-500">
              Harga jual seluruh katalog, disandingkan dengan posisi stok.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Tampil kalau server mengizinkan Mode Daftar Harga UNTUK akun ini,
              ATAU sedang aktif (supaya menyalakannya lalu kehilangan haknya
              di tengah jalan tetap bisa dimatikan kembali). */}
          {(data?.boleh_mode_daftar_harga || modeDaftarHarga) && (
            <button type="button" onClick={() => setModeDaftarHarga(v => !v)}
              data-testid="pl-mode-daftar-harga"
              title="Sembunyikan modal & margin, termasuk dari file Excel — aman dikirim ke sales"
              className={`h-10 px-3 rounded-lg text-sm font-medium border inline-flex items-center gap-2 ${
                modeDaftarHarga ? "bg-amber-50 border-amber-200 text-amber-700"
                                : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {modeDaftarHarga ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              Mode Daftar Harga
            </button>
          )}
          {/* Gerbang ROLE, sama dengan backend `require_role("superadmin")" —
              admin pun tidak melihat tombol ini (aturan 3 CLAUDE.md). */}
          {bolehAturAkses && (
            <button type="button" onClick={() => setAturAksesTerbuka(true)}
              data-testid="pl-atur-akses"
              title="Atur siapa saja yang melihat modal & margin di Price List, Master Produk, Bundling, dan penjualan"
              className="h-10 px-3 rounded-lg text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              Atur Akses Modal &amp; Margin
            </button>
          )}
          {/* G2: saklar jawaban Bos no. 23 — gerbang peran yang SAMA. */}
          {bolehAturAkses && (
            <button type="button" onClick={() => setAturHargaUnitTerbuka(true)}
              data-testid="pl-atur-harga-unit"
              title="Jawaban pertanyaan no. 23: kolom harga jual per IMEI & isi otomatis di nota"
              className="h-10 px-3 rounded-lg text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              Atur Harga per IMEI
            </button>
          )}
          <button type="button" onClick={unduhExcel} disabled={unduh}
            data-testid="pl-export-xlsx"
            className="h-10 px-4 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-2">
            <Download className="w-4 h-4" />
            {unduh ? "Menyiapkan…" : "Export Excel"}
          </button>
        </div>
      </div>

      {aturAksesTerbuka && (
        <PengaturanAksesModal onClose={() => setAturAksesTerbuka(false)} onTersimpan={muat} />
      )}
      {aturHargaUnitTerbuka && (
        <PengaturanHargaUnit onClose={() => setAturHargaUnitTerbuka(false)} onTersimpan={muat} />
      )}

      {/* Filter — baris pertama SAMA PERSIS dengan Master Produk (komponen
          bersama); cabang & "Belum ada harga" khas Price List, ditempel di
          ujungnya. Gudang hanya ditawarkan kalau izin daftar gudangnya ada. */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-3">
        <FilterProduk className="flex flex-wrap items-center gap-2"
                      nilai={filter} ubah={ubahFilter} opsi={opsiFilter}
                      cari={{ nilai: qInput, onChange: setQInput, onEnter: () => setQApplied(qInput),
                              // G2: kotak cari juga menemukan barang lewat IMEI/SN (>= 5 karakter).
                              placeholder: "Cari SKU / nama / barcode / IMEI / SN" }}
                      gudang={bolehGudang ? gudang : []} suppliers={suppliers}
                      gudangGagal={bolehGudang && gudangGagal} supplierGagal={supplierGagal}
                      agingHari={data?.aging_hari || 60} testid={TESTID_PRICE_LIST}>
          <select value={cabang} onChange={e => setCabang(e.target.value)}
            data-testid="pl-cabang" aria-label="Filter cabang"
            className="h-9 px-2 border border-slate-200 rounded-lg text-sm">
            <option value="">Semua cabang</option>
            {cabangOpsi.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {/* Dulu salah satu isi dropdown Status. Aktif/nonaktif kini ikut
              "Termasuk nonaktif" milik Master Produk, jadi "belum ada harga"
              berdiri sendiri dan DI-DAN-kan dengan filter lain. */}
          <button type="button" onClick={() => setTanpaHarga(v => !v)} data-testid="pl-tanpa-harga"
            title="Hanya barang yang harga jualnya belum diisi"
            className={`h-9 px-3 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 ${
              tanpaHarga ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            <AlertTriangle className="w-3.5 h-3.5" /> Belum ada harga
          </button>
        </FilterProduk>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            {[{ v: "sku", l: "Per barang", i: Tags }, { v: "gudang", l: "Rinci per gudang", i: Warehouse },
              { v: "imei", l: "Rinci per IMEI/SN", i: Smartphone }].map(o => (
              <button key={o.v} type="button" onClick={() => setRinci(o.v)}
                data-testid={`pl-rinci-${o.v}`}
                className={`px-3 h-9 inline-flex items-center gap-2 ${
                  rinci === o.v ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                <o.i className="w-4 h-4" />{o.l}
              </button>
            ))}
          </div>
          {/* "Hanya yang ada stok" diganti filter Stok milik Master Produk
              (Ada stok > 0) di baris atas. */}
          <select value={urut} onChange={e => setUrut(e.target.value)}
            data-testid="pl-urut" className={`${inp} h-9`}>
            {urutOpsi.map(o => <option key={o.v} value={o.v}>Urut: {o.l}</option>)}
          </select>
          <button type="button" onClick={() => setArah(arah === "asc" ? "desc" : "asc")}
            data-testid="pl-arah" className={`${inp} h-9 hover:bg-slate-50`}>
            {arah === "asc" ? "A → Z / kecil → besar" : "Z → A / besar → kecil"}
          </button>
          <button type="button" onClick={bersihkan} data-testid="pl-bersihkan"
            className="h-9 px-3 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 inline-flex items-center gap-1">
            <X className="w-3.5 h-3.5" /> Bersihkan filter
          </button>
        </div>
      </div>

      {/* Catatan jujur dari server (mis. gudang serba-cabang, modal disembunyikan) */}
      {(data?.catatan || []).map((c, i) => (
        <div key={i} data-testid="pl-catatan"
          className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          <Info className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
          <span>{c}</span>
        </div>
      ))}

      {/* Tabel */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm" data-testid="pl-kosong">
            <Boxes className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            Tidak ada barang yang cocok dengan filter ini.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pl-tabel">
              <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                <tr>
                  <Th kol="sku">SKU</Th>
                  <Th kol="nama">Nama Barang</Th>
                  <Th kol="kategori">Kategori</Th>
                  <Th kol="brand">Brand</Th>
                  {modeImei && (
                    <>
                      <th data-testid="pl-kol-imei" className="px-3 py-2 text-left font-semibold whitespace-nowrap">IMEI/SN</th>
                      {/* Judul VERBATIM brief Bos (22 Sep, hal. 17). */}
                      <th data-testid="pl-kol-bh" className="px-3 py-2 text-left font-semibold whitespace-nowrap">Battery Health</th>
                      <th data-testid="pl-kol-garansi" className="px-3 py-2 text-left font-semibold whitespace-nowrap">Garansi</th>
                    </>
                  )}
                  <Th kol="harga" kanan>Harga Jual</Th>
                  <Th kol="stok" kanan>Stok</Th>
                  <Th kol="gudang">Gudang</Th>
                  <Th kol="cabang">Cabang</Th>
                  {modalTerlihat && (
                    <th data-testid="pl-kol-modal" className="px-3 py-2 text-right font-semibold whitespace-nowrap">Modal/Unit</th>
                  )}
                  {marginBentuk && <Th kol="margin" kanan testid="pl-kol-margin">Margin</Th>}
                  {kolomAksi && <th className="px-2 py-2" aria-label="Aksi" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it, i) => (
                  <Fragment key={`${it.sku}-${it.warehouse_id || "all"}-${i}`}>
                  <tr data-testid={`pl-baris-${it.sku}`}
                    className="hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-mono text-xs text-slate-500 whitespace-nowrap">{it.sku}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{it.nama}</div>
                      {!it.aktif && <span className="text-[11px] text-slate-400">Nonaktif</span>}
                      {it.cocok_lewat_imei && (
                        <span data-testid={`pl-cocok-imei-${it.sku}`}
                          className="ml-1 text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100">
                          cocok lewat IMEI/SN
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                      {labelKategori[it.kategori] || it.kategori || "—"}
                      {it.sub_kategori && <span className="text-slate-400"> / {it.sub_kategori}</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{it.brand || "—"}</td>
                    {modeImei && (
                      <>
                        <td className="px-3 py-2 text-slate-500 whitespace-nowrap text-xs" data-testid={`pl-unit-jumlah-${it.sku}`}>
                          {it.paket_virtual ? "paket"
                            // Barang tanpa IMEI (tanpa `selisih_unit_stok`) tidak punya daftar unit.
                            : (it.unit_total || it.selisih_unit_stok !== undefined ? `${it.unit_total || 0} unit` : "—")}
                          {it.selisih_unit_stok ? (
                            <span data-testid={`pl-selisih-imei-${it.sku}`}
                              title="Kolom Stok (kartu stok) tidak sama dengan jumlah IMEI terdaftar — kemungkinan ada nota yang tidak mengurangi kartu stok, atau unit lama tanpa mutasi masuk. Laporkan ke Finance/Gudang."
                              className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                              Stok ≠ IMEI ({it.selisih_unit_stok > 0 ? "+" : ""}{it.selisih_unit_stok})
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2" />
                        <td className="px-3 py-2" />
                      </>
                    )}
                    <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                      {it.harga_jual > 0 ? idr(it.harga_jual) : (
                        <span className="text-amber-600 inline-flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" />belum diisi
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {it.paket_virtual ? (
                        /* Paket: jumlah paket yang bisa dibentuk dari stok komponen
                           dalam cakupan filter — angka TERPISAH (`stok_paket`), bukan
                           kolom Stok barang fisik. */
                        <span data-testid={`pl-stok-paket-${it.sku}`}
                          title={`Dihitung dari stok komponen${it.paket_pembatas ? ` (pembatas: ${it.paket_pembatas})` : ""}`}>
                          <span className={it.stok_paket > 0 ? "text-slate-800" : "text-slate-400"}>{it.stok_paket}</span>
                          <span className="ml-1 text-[10px] px-1 rounded bg-violet-50 text-violet-700 border border-violet-100">paket</span>
                        </span>
                      ) : (
                        <span className={it.stok > 0 ? "text-slate-800" : "text-slate-400"}>{it.stok}</span>
                      )}
                      {it.stok_rusak > 0 && (
                        <span className="text-[11px] text-rose-500 ml-1" title="unit rusak, tidak layak jual">
                          ({it.stok_rusak} rusak)
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600 max-w-[220px] truncate" title={
                      (it.gudang_daftar || []).join(", ") || it.gudang || ""}>
                      {it.gudang || "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                      {it.cabang || "—"}
                      {it.cabang_via && (
                        <span className="ml-1 text-[11px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-100"
                          title="Sebagian stok berada di gudang yang melayani semua cabang">
                          <Building2 className="w-3 h-3 inline mr-0.5" />{it.cabang_via}
                        </span>
                      )}
                    </td>
                    {modalTerlihat && (
                      <td className="px-3 py-2 text-right whitespace-nowrap" data-testid={`pl-sel-modal-${it.sku}${it.warehouse_id ? `-${it.warehouse_id}` : ""}`}>
                        {it.modal == null ? <span className="text-slate-400">—</span> : (
                          <>
                            {idr(it.modal)}
                            {it.modal_sumber === "perkiraan" && (
                              <span className="block text-[11px] text-amber-600">perkiraan</span>
                            )}
                          </>
                        )}
                      </td>
                    )}
                    {marginBentuk === "persen" && (
                      <td className="px-3 py-2 text-right whitespace-nowrap" data-testid={`pl-sel-margin-${it.sku}${it.warehouse_id ? `-${it.warehouse_id}` : ""}`}>
                        {it.margin_persen == null ? (
                          <span className="text-slate-400" title="modal belum diketahui — bukan berarti untung">—</span>
                        ) : (
                          <span className={it.rugi ? "text-rose-600 font-semibold" : "text-slate-800"}>
                            {it.margin_persen.toFixed(1)}%
                          </span>
                        )}
                      </td>
                    )}
                    {marginBentuk === "kelas" && (
                      <td className="px-3 py-2 text-right whitespace-nowrap" data-testid={`pl-sel-margin-${it.sku}${it.warehouse_id ? `-${it.warehouse_id}` : ""}`}>
                        <span data-testid={`pl-kelas-${it.sku}${it.warehouse_id ? `-${it.warehouse_id}` : ""}`}
                          title={it.margin_kelas?.kode === "belum_diketahui" ? "modal belum diketahui — bukan berarti untung" : ""}
                          className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full border ${KELAS_WARNA[it.margin_kelas?.kode] || KELAS_WARNA.belum_diketahui}`}>
                          {it.margin_kelas?.kode === "belum_diketahui" ? "—" : (it.margin_kelas?.label || "—")}
                        </span>
                      </td>
                    )}
                    {kolomAksi && <td className="px-2 py-2" />}
                  </tr>
                  {modeImei && (it.unit || []).map(u => (
                    <BarisUnit key={u.id} unit={u} modalTerlihat={modalTerlihat} marginBentuk={marginBentuk}
                      bolehEdit={bolehUbahUnit} kolomHargaAktif={kolomHargaUnit} kolomAksi={kolomAksi}
                      onSimpan={simpanUnit} />
                  ))}
                  {modeImei && it.unit_terpotong && (
                    <tr data-testid={`pl-unit-terpotong-${it.sku}`} className="bg-slate-50/40">
                      <td colSpan={jumlahKolom} className="px-3 py-1.5 text-xs text-slate-500">
                        …dan {(it.unit_total || 0) - (it.unit || []).length} unit lain — ketik IMEI/SN di kotak
                        cari, atau unduh Excel untuk daftar lengkap.
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginasi — angka totalnya dari server, bukan dihitung dari baris yang
          sedang tampil (baris yang tampil sudah dipotong halaman). */}
      {data && data.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600">
          <div data-testid="pl-ringkasan">
            Menampilkan <b>{items.length}</b> dari <b>{data.total}</b> barang
            {data.rinci === "gudang" && " (dirinci per gudang)"}
            {data.rinci === "imei" && " (dengan daftar IMEI/SN per barang)"}
            {data.tanpa_harga_di_halaman > 0 && (
              <span className="text-amber-600"> · {data.tanpa_harga_di_halaman} belum ada harga jual</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              data-testid="pl-prev"
              className="h-9 px-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2">Hal {data.page} / {data.total_pages}</span>
            <button type="button" disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)}
              data-testid="pl-next"
              className="h-9 px-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
